import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ShieldCheck,
    Camera,
    Blinds,
    Star,
    MapPin,
    Award,
    Clock,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import QuoteDialog from "@/components/QuoteDialog";
import { BUSINESS, IMAGES } from "@/lib/business";
import { CTAStripe, ProcessSteps } from "@/components/sections/Shared";
import SEO, { LOCAL_BUSINESS_JSONLD } from "@/components/SEO";

const SERVICES = [
    {
        icon: ShieldCheck,
        title: "Security Window Films",
        description:
            "Impact-resistant films that reinforce existing glass, deter break-ins and block UV.",
        image: "https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/zkv58ykf_installation%20pic%20-%20GTA%20Secure.png",
        to: "/security-films/residential",
        cta: "Explore Films",
    },
    {
        icon: Camera,
        title: "Security Cameras",
        description:
            "HD weather-ready cameras with smart detection and mobile alerts for peace of mind.",
        image: IMAGES.cameras,
        to: "/security-cameras",
        cta: "Explore Cameras",
    },
    {
        icon: Blinds,
        title: "Blinds & Shutters",
        description:
            "Custom-made blinds, shades and shutters that complete your space, made to measure.",
        image: "https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/l190ucjk_zebra%20blinds%20gta%20secure.png",
        to: "/blinds-shutters",
        cta: "Explore Blinds",
    },
];

const TESTIMONIALS = [
    {
        name: "Priya S.",
        city: "Mississauga, ON",
        quote:
            "GTA Secure installed security film on every ground-floor window and added cameras. Clean install, fair price, and the team was genuinely helpful.",
    },
    {
        name: "Daniel R.",
        city: "Oakville, ON",
        quote:
            "They fitted our entire living room with custom blinds and added a camera at the back gate. Professional from quote to cleanup.",
    },
    {
        name: "Meera & James",
        city: "Brampton, ON",
        quote:
            "We had a break-in attempt last winter — the security film on our patio door held. It paid for itself in one night. Highly recommend.",
    },
];

const FAQS = [
    {
        q: "Do you service both homes and businesses?",
        a: "Yes. We install window films, security cameras, blinds and shutters for residential, commercial and multi-unit properties across the Greater Toronto Area.",
    },
    {
        q: "Will security film change the look of my windows?",
        a: "Clear safety films are virtually invisible — the glass keeps the same view and light transmission. Privacy, solar and decorative films add the finish you choose.",
    },
    {
        q: "How long does installation take?",
        a: "Most residential projects are completed the same day. Larger commercial installs are scheduled to minimise disruption. We confirm a timeline with your quote.",
    },
    {
        q: "Are quotes really free?",
        a: "Yes. We provide a no-obligation, transparent quote after an on-site or virtual assessment. No pressure, no upsell.",
    },
    {
        q: "Where are you located?",
        a: `We are based at ${BUSINESS.address.full}, and serve the entire Greater Toronto Area.`,
    },
];

export default function Home() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Window Films, Security Cameras & Custom Blinds — GTA"
                description="GTA Secure Inc. — Canadian-owned professional installer of security window films, camera systems, and custom blinds & shutters. Serving homes and businesses across the Greater Toronto Area."
                path="/"
                keywords="GTA security, window film Toronto, security cameras GTA, blinds shutters Mississauga, Georgetown security company, Canadian-owned, window film installation"
                jsonLd={LOCAL_BUSINESS_JSONLD}
            />
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="container-x pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
                    <div className="reveal-up">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> GTA Secure Inc. — Canadian owned
                        </p>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[4rem] font-bold text-navy leading-[1.02]">
                            Protect what matters. <br />
                            <span className="text-gold">Beautifully.</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                            Premium security window films, smart camera systems, and custom
                            blinds &amp; shutters — installed with care across the Greater
                            Toronto Area.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                data-testid="hero-quote-button"
                                onClick={() => setQuoteOpen(true)}
                                className="btn-gold"
                            >
                                Get a Free Quote <ArrowRight className="h-4 w-4" />
                            </button>
                            <a
                                data-testid="hero-phone-link"
                                href={BUSINESS.phoneHref}
                                className="btn-outline-navy"
                            >
                                Call {BUSINESS.phone}
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-8 text-sm">
                            <Stat big="1500+" label="Homes Protected" />
                            <div className="h-10 w-px bg-gray-200" />
                            <Stat big="200+" label="Businesses Secured" />
                            <div className="h-10 w-px bg-gray-200 hidden sm:block" />
                            <Stat big="9+ yrs" label="Experience" />
                        </div>
                    </div>
                    <div className="relative reveal-up reveal-up-delay-2">
                        <div className="aspect-[4/5] w-full overflow-hidden rounded-[32px] bg-navy/5">
                            <img
                                src={IMAGES.hero}
                                alt="Modern house protected by GTA Secure"
                                className="h-full w-full object-cover"
                                loading="eager"
                            />
                        </div>
                        <div className="absolute -bottom-6 -right-6 hidden md:flex items-center gap-3 bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
                            <div className="h-10 w-10 rounded-full bg-[#FDFCFB] border border-gold/30 flex items-center justify-center">
                                <Star className="h-4 w-4 text-gold fill-[#C9A961]" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-navy">5.0 rating</p>
                                <p className="text-xs text-slate-500">from 300+ GTA customers</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="section bg-white">
                <div className="container-x">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                                <span className="gold-rule" /> Our Services
                            </p>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy max-w-2xl leading-tight">
                                Three expert solutions, one trusted team.
                            </h2>
                        </div>
                        <p className="max-w-md text-slate-600">
                            From invisible safety films to beautiful custom blinds, we help you
                            secure and style every opening in your home or workplace.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    to={s.to}
                                    key={s.title}
                                    data-testid={`service-card-${i}`}
                                    className="card-premium group block"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-navy/5">
                                        <img
                                            src={s.image}
                                            alt={s.title}
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-7">
                                        <div className="h-10 w-10 rounded-xl bg-[#FDFCFB] border border-gold/30 text-gold flex items-center justify-center mb-4">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h3 className="font-display text-2xl font-semibold text-navy">
                                            {s.title}
                                        </h3>
                                        <p className="mt-2.5 text-slate-600 text-sm leading-relaxed">
                                            {s.description}
                                        </p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                                            {s.cta} <ArrowRight className="h-4 w-4" />
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> How we work
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            A simple, honest process — from quote to installation.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                {
                                    title: "Get a quote",
                                    description:
                                        "Tell us about your space. We'll send a transparent, no-obligation quote — fast.",
                                },
                                {
                                    title: "Professional installation",
                                    description:
                                        "Our trained installers handle the job cleanly, precisely, and respectfully of your property.",
                                },
                                {
                                    title: "Feel safe & at home",
                                    description:
                                        "Enjoy better security, comfort, and style — backed by our workmanship guarantee.",
                                },
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                    <div className="order-2 lg:order-1 relative">
                        <div className="aspect-[5/4] overflow-hidden rounded-[28px] bg-navy/5">
                            <img
                                src="https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/4tmlnot2_home%20page%20gta%20secure%20why%20us.png"
                                alt="Quality workmanship"
                                className="h-full w-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Why GTA Secure
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Canadian-owned. Locally trusted.
                        </h2>
                        <p className="mt-5 text-slate-600 max-w-xl">
                            Headquartered in Georgetown, Ontario, we're a fully Canadian-owned
                            team that cares as much about the finish as the function. Every
                            project is handled with precision, honesty, and long-term support.
                        </p>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <WhyItem icon={MapPin} title="Local GTA team" desc="Serving Greater Toronto Area homes & businesses." />
                            <WhyItem icon={Award} title="Certified installers" desc="Trained, licensed, and fully insured professionals." />
                            <WhyItem icon={ShieldCheck} title="Premium products" desc="Top-tier security films, cameras & custom blinds." />
                            <WhyItem icon={Clock} title="On-time delivery" desc="Clear timelines. Clean installs. Respectful of your space." />
                        </div>
                    </div>
                </div>
            </section>

            {/* GALLERY */}
            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                                <span className="gold-rule" /> Portfolio
                            </p>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy max-w-2xl leading-tight">
                                Real installs, real transformations.
                            </h2>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="gallery-contact-link"
                            className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all"
                        >
                            Start your project <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                        {IMAGES.gallery.map((src, idx) => (
                            <div
                                key={src}
                                data-testid={`gallery-item-${idx}`}
                                className={`overflow-hidden rounded-2xl bg-navy/5 ${
                                    idx === 0 || idx === 3 ? "md:row-span-2 aspect-[3/4] md:aspect-[3/5]" : "aspect-[4/3]"
                                }`}
                            >
                                <img
                                    src={src}
                                    alt={`Gallery ${idx + 1}`}
                                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-700"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Reviews
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Homeowners &amp; businesses across the GTA trust us.
                        </h2>
                    </div>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={t.name}
                                data-testid={`testimonial-${i}`}
                                className="card-premium p-7"
                            >
                                <div className="flex items-center gap-1 text-gold">
                                    {[...Array(5)].map((_, j) => (
                                        <Star key={j} className="h-4 w-4 fill-[#C9A961]" />
                                    ))}
                                </div>
                                <p className="mt-4 text-slate-700 leading-relaxed">"{t.quote}"</p>
                                <div className="mt-6 pt-5 border-t border-gray-100">
                                    <p className="font-semibold text-navy">{t.name}</p>
                                    <p className="text-sm text-slate-500">{t.city}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section bg-[#FDFCFB]">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Quick answers, honest guidance.
                        </h2>
                        <p className="mt-5 text-slate-600 max-w-md">
                            Still have questions? Call us directly at{" "}
                            <a
                                href={BUSINESS.phoneHref}
                                className="text-gold font-semibold hover:underline"
                            >
                                {BUSINESS.phone}
                            </a>
                            .
                        </p>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                value={`faq-${i}`}
                                key={f.q}
                                data-testid={`faq-item-${i}`}
                                className="border-b border-gray-200"
                            >
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CTAStripe
                heading="Ready to secure and beautify your space?"
                subheading="Free on-site assessment. No pressure, ever."
                ctaLabel="Request Free Quote"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
        </>
    );
}

function Stat({ big, label }) {
    return (
        <div>
            <p className="font-display text-2xl md:text-3xl font-bold text-navy">{big}</p>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">{label}</p>
        </div>
    );
}

function WhyItem({ icon: Icon, title, desc }) {
    return (
        <div className="flex gap-3">
            <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-[#FDFCFB] border border-gold/30 text-gold flex items-center justify-center">
                <Icon className="h-4 w-4" />
            </div>
            <div>
                <p className="font-semibold text-navy">{title}</p>
                <p className="text-sm text-slate-600 mt-0.5">{desc}</p>
            </div>
        </div>
    );
}
